import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useMediaQuery } from 'react-responsive';

import { formatPhoneNumber } from 'common/formatters';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  MEDIA_QUERY_SMALL_SCREEN,
  MEDIA_QUERY_MEDIUM_SCREEN_850,
} from 'client/constants/mediaQueries';

import {
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
} from 'common/householdConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrUserNameCircle, {
  USER_NAME_ORANGE_THEME,
  USER_NAME_GREEN_THEME,
} from 'client/components/SrUserNameCircle/SrUserNameCircle';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_BLUR,
} from 'client/components/SrButton/SrButton';

const getHouseholdCardMoment = ({ household }) => {
  const firstApplication = household.householdApplications[0];
  if (!firstApplication) {
    return null;
  }
  return momentWithAustinTimezone(firstApplication.createdDate);
};

const getHouseholdCardDateFormmated = (householdCardMoment) => {
  if (!householdCardMoment) {
    return '';
  }

  const householdCardDateFormatted = householdCardMoment.calendar(null, {
    sameDay: '[Today]',
    lastDay: '[Yesterday]',
    lastWeek: 'MMM Do',
    sameElse: 'MMM Do',
  });

  return householdCardDateFormatted;
};

const getApplicationThemeByStatus = (applicationStatus) => {
  const statusMapping = {
    [HOUSEHOLD_APPLICATION_STATUS_REQUESTED]: USER_NAME_ORANGE_THEME,
    [HOUSEHOLD_APPLICATION_STATUS_STARTED]: USER_NAME_ORANGE_THEME,
    [HOUSEHOLD_APPLICATION_STATUS_COMPLETED]: USER_NAME_GREEN_THEME,
  };

  return statusMapping[applicationStatus] || USER_NAME_GREEN_THEME;
};

const getApplicationUserNameColor = (applicationStatus) => {
  const statusMapping = {
    [HOUSEHOLD_APPLICATION_STATUS_REQUESTED]: 'orange300',
    [HOUSEHOLD_APPLICATION_STATUS_STARTED]: 'orange300',
    [HOUSEHOLD_APPLICATION_STATUS_COMPLETED]: 'green',
  };

  return statusMapping[applicationStatus] || 'green';
};

const getApplicationStatusText = (application, household) => {
  if (
    household.leadRenterId !== application.userId &&
    application.status === HOUSEHOLD_APPLICATION_STATUS_REQUESTED
  ) {
    return 'Invited to Apply';
  }

  const statusMapping = {
    [HOUSEHOLD_APPLICATION_STATUS_REQUESTED]: 'Requested App',
    [HOUSEHOLD_APPLICATION_STATUS_STARTED]: 'Started App',
    [HOUSEHOLD_APPLICATION_STATUS_COMPLETED]: 'Completed App',
  };

  return statusMapping[application.status] || 'Completed App';
};

const SrPartnerHouseholdStartApplicationCard = ({
  household,
  users,
  renterApplications,
  guarantorsApplications,
}) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });
  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN_850,
  });

  const [userContactModal, setUserContactModal] = useState(null);
  const householdCardMoment = getHouseholdCardMoment({ household });
  const householdDateFormatted = getHouseholdCardDateFormmated(householdCardMoment);
  const applications = [
    ...renterApplications,
    ...guarantorsApplications.map((g) => ({ ...g, isGuarantor: true })),
  ];
  const onContactApplicant = (application) => {
    const renterInfo = users.find(({ id }) => id === application.userId);

    setUserContactModal({ ...application, ...renterInfo });
  };

  return (
    <>
      <div
        className={classnames(
          'SrPartnerHouseholdPendingApprovalCard',
          `pendingApprovalId-${household.id}`,
        )}
      >
        <SrBox padding="18px 18px 8px 18px" className="SrPartnerHouseholdPendingApprovalCardHeader">
          <SrText
            weight="800"
            display="block"
            font="greycliff"
            fontSize="26px"
            lineHeight="25px"
            cursor={null}
          >
            Application #{household.id}
          </SrText>
          <SrFlex justify="between" align="end">
            <SrBox marginTop="5px">
              <SrText
                color="blueGreycliff"
                opacity="0.5"
                font="greycliff"
                weight="800"
                fontSize="16px"
                lineHeight="25px"
              >
                Started {householdDateFormatted}
              </SrText>
            </SrBox>
          </SrFlex>
        </SrBox>
        <SrSeparator />
        <SrBox padding="8px 20px 27px">
          <SrBox marginBottom="12px">
            <SrText
              color="blueGreycliff"
              font="greycliff"
              weight="800"
              fontSize="16px"
              lineHeight="25px"
            >
              Applicants
            </SrText>
          </SrBox>
          {applications.map((application) => {
            const applicantName = `${application.firstName} ${application.lastName}`;
            const theme = getApplicationThemeByStatus(application.status);
            const colorName = getApplicationUserNameColor(application.status);
            const statusText = getApplicationStatusText(application, household);

            return (
              <SrFlex key={`started-app-${application.id}-${household.id}`} marginBottom="12px">
                <SrFlex width="calc(100% - 73px)">
                  <SrBox>
                    <SrUserNameCircle name={applicantName} theme={theme} />
                  </SrBox>
                  <SrFlex direction="col" overflow="hidden" paddingRight="5px">
                    <SrFlex>
                      <SrText
                        weight="600"
                        font="greycliff"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        lineHeight="24px"
                      >
                        {applicantName}
                      </SrText>
                      {application.isGuarantor && (
                        <SrText fontStyle="italic" color="grey" lineHeight="24px">
                          &nbsp;(Guarantor)
                        </SrText>
                      )}
                    </SrFlex>
                    <SrText color={colorName} weight="700" font="greycliff" lineHeight="24px">
                      {statusText}
                    </SrText>
                  </SrFlex>
                </SrFlex>
                <SrFlex width="73px" align="center">
                  <button
                    className="contactBtn"
                    type="button"
                    onClick={() => onContactApplicant(application)}
                  >
                    Contact
                  </button>
                </SrFlex>
              </SrFlex>
            );
          })}
        </SrBox>
      </div>
      {userContactModal && (
        <SrModalV2
          centerContent
          closeOnBackgroundClick
          showCloseButton={false}
          contentMaxWidth={!isSmallScreen ? '375px' : null}
          padding={isSmallScreen ? 'calc(100vh - 660px) 0 0 0' : '0 0 120px 0'}
          contentPadding="0"
          onClose={() => setUserContactModal(null)}
          theme={isSmallScreen ? MODAL_THEME_FIXED_FOOTER_BUTTON : null}
          buttonTheme={isSmallScreen ? SR_BUTTON_THEME_ADOBE_GREY : SR_BUTTON_THEME_BLUR}
        >
          <SrBox
            borderRadius={isMediumScreen ? '14px' : '24px'}
            backgroundColor="white"
            boxShadow="0px 15px 26px rgba(212, 212, 212, 0.17)"
            boxSizing="border-box"
            maxWidth="375px"
            width={isSmallScreen ? 'calc(100vw - 20px)' : '375px'}
            padding="48px 16px 40px"
            margin={isMediumScreen ? '0 auto' : null}
          >
            <SrBox
              margin="0 auto"
              backgroundSize="cover"
              backgroundRepeat="no-repeat"
              backgroundPosition="center"
              backgroundImage="url(/images/phone-blue-calling.svg)"
              width="97px"
              height="96px"
              borderRadius="64px"
              position="relative"
              zIndex="5"
              marginBottom="28px"
            />
            <SrFlex justify="center" direction="col">
              <SrFlex marginBottom="16px" justify="center">
                <SrText
                  color="blueGreycliff"
                  font="greycliff"
                  weight="800"
                  fontSize="26px"
                  lineHeight="33px"
                  align="center"
                >
                  {userContactModal.firstName} {userContactModal.lastName}
                </SrText>
              </SrFlex>
              <SrFlex marginBottom="40px" justify="center" direction="col">
                <SrText
                  color="blueGreycliff"
                  font="greycliff"
                  weight="600"
                  fontSize="16px"
                  lineHeight="24px"
                  align="center"
                >
                  Contact Info:
                </SrText>
                <SrText
                  color="blueGreycliff"
                  font="greycliff"
                  weight="800"
                  fontSize="16px"
                  lineHeight="24px"
                  align="center"
                >
                  {formatPhoneNumber(userContactModal.phone)}
                </SrText>
                <SrText
                  color="blueGreycliff"
                  font="greycliff"
                  weight="800"
                  fontSize="16px"
                  lineHeight="24px"
                  align="center"
                >
                  {userContactModal.email}
                </SrText>
              </SrFlex>
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREY}
                onClick={() => setUserContactModal(null)}
              >
                Close
              </SrButton>
            </SrFlex>
          </SrBox>
        </SrModalV2>
      )}
    </>
  );
};

SrPartnerHouseholdStartApplicationCard.propTypes = {
  household: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  renterApplications: PropTypes.array.isRequired,
  guarantorsApplications: PropTypes.array.isRequired,
};

SrPartnerHouseholdStartApplicationCard.displayName = 'SrPartnerHouseholdStartApplicationCard';

export default SrPartnerHouseholdStartApplicationCard;
